/**
 * Settlement predicates for "APA owes Operations" tracking.
 *
 * When a charter expense is paid with Operations resources (Boat Cash, Boat Stock,
 * Personal funds) or Non AnkorPay Card / Non AnkorPay Bank Transfer, it creates a debt that
 * APA must repay. These predicates determine which transactions require settlement
 * tracking upon approval.
 *
 */
import {
  Transaction,
  PaymentMethod,
  TransactionType,
} from '@ankor-io/common/types/transaction/transaction'

/**
 * Payment methods that indicate Operations resources were used for a charter expense.
 */
export const SettlementTriggerPaymentMethods = [
  PaymentMethod.BOAT_CASH,
  PaymentMethod.BOAT_STOCK,
  PaymentMethod.PERSONAL_CASH_OR_CARD,
  PaymentMethod.BOAT_APA_CARD,
  PaymentMethod.NON_ANKORPAY_BANK_TRANSFER,
]

export const hasSettlementTriggerPaymentMethod = (t: Pick<Transaction, 'paymentMethod'>): boolean => {
  return SettlementTriggerPaymentMethods.includes(t.paymentMethod)
}

/**
 * Gratuity always requires settlement - it's generated at charter sign-off
 * and must be transferred from APA to Operations for crew distribution.
 */
export const isGratuity = (t: Pick<Transaction, 'type'>): boolean => {
  return t.type === TransactionType.GRATUITY
}

/**
 * Determines if a transaction should have settlementStatus set to PENDING on approval.
 *
 * Note: Caller must verify the transaction is in an APA account before calling this.
 */
export const shouldSetPendingSettlement = (t: Pick<Transaction, 'paymentMethod' | 'type'>): boolean => {
  // TODO: For APA CASH FLOATS:
  // Add a type-guard here that returns false for CASH_FLOAT_TOP_UP and CASH_FLOAT_TRANSFER.
  // Today every cash float is OPS-side so this predicate is never called for those types.
  // But once APA cash floats exist, a transfer source row (paymentMethod = BOAT_CASH on
  // an APA account) will be incorrectly marked APA-owes-OPS PENDING without this guard.
  // Fix:
  //   if (t.type === TransactionType.CASH_TOP_UP || t.type === TransactionType.CASH_FLOAT_TRANSFER) return false
  if (isGratuity(t)) return true
  return hasSettlementTriggerPaymentMethod(t)
}
  